import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/services/api";
import useStore from "@/services/store";
import { timeSince } from "@/utils";
import Loader from "@/components/loader";
import { MdLocationOn, MdWork, MdAttachMoney, MdAccessTime } from "react-icons/md";
import { FiBriefcase } from "react-icons/fi";
import toast from "react-hot-toast";
import { useComponentTranslation } from "@/services/i18n";

const translations = {
  en: {
    job: {
      application: {
        title: "My Applications",
        subtitle: "Here are the offers you have applied to",
        error_fetch_applications: "Failed to fetch applications",
      },
    },
    candidate_home: {
      posted: "Posted",
      no_jobs: "No matching jobs found",
    },
  },
  fr: {
    job: {
      application: {
        title: "Mes candidatures",
        subtitle: "Voici les offres auxquelles vous avez postulé",
        error_fetch_applications: "Échec du chargement des candidatures",
      },
    },
    candidate_home: {
      posted: "Publié il y a",
      no_jobs: "Aucune offre correspondante trouvée",
    },
  },
};

export default function List() {
  const { t } = useComponentTranslation(translations);
  const { user } = useStore();
  const [jobs, setJobs] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const { ok, data } = await api.post("/application/search", {
          limit: 10,
          candidate_id: user._id,
          page: 1,
          organization_name: true,
        });
        if (!ok) return;
        setJobs(data);
        setLoading(false);
      } catch (e) {
        console.error(e);
        toast.error(e.code);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading)
    return (
      <div className="flex mt-20 justify-center">
        <Loader />
      </div>
    );

  return (
    <>
      {/* <h1 className="text-2xl font-bold">{t("job.application.title")}</h1>
      <p className="text-sm mb-4 text-gray-800 mt-1 font-light">{t("job.application.subtitle")}</p> */}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {jobs?.length > 0 ? jobs.map((job) => <JobCard key={job._id} application={job} />) : <div className="text-center py-12 text-gray-500">{t("candidate_home.no_jobs")}</div>}
      </div>
    </>
  );
}

const JobCard = ({ application }) => {
  const { t } = useComponentTranslation(translations);

  const [job, setJob] = useState(null);

  const getJob = async () => {
    const { ok, data } = await api.get(`/job/${application.job_id}`);
    if (!ok) return;
    setJob(data);
  };

  useEffect(() => {
    getJob();
  }, []);

  if (!job) return <></>;

  return (
    <Link to={`/candidate/application/${application._id}`} className="group bg-white border border-gray-200 rounded-xl p-6 hover:border-primary transition-all duration-300">
      {/* Icon */}
      <div className="flex items-center justify-between mb-2">
        <div className="shrink-0 w-12 h-12 bg-gray-50 rounded-lg flex items-center justify-center">
          <FiBriefcase className="w-6 h-6 text-gray-600" />
        </div>
        <div className="inline-flex items-center px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-xs font-medium">{t(application.status)}</div>
      </div>
      <div className="flex items-start gap-4 mt-4">
        {/* Content */}
        <div className="flex-1 min-w-0">
          {/* Title */}
          <h3 className="text-lg font-semibold text-gray-900 mb-2 group-hover:text-primary transition-colors">{job.title}</h3>

          {/* Company Name */}
          <div className="flex items-center gap-2 mb-4">
            <span className="text-sm font-medium text-primary">{job.organization_name}</span>
          </div>

          {/* Description/Metadata */}
          <div className="flex flex-col gap-2 mb-4">
            {job.address && (
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <MdLocationOn className="h-4 w-4 text-gray-400 shrink-0" />
                <span className="truncate">{job.address}</span>
              </div>
            )}

            <div className="flex items-center gap-4 flex-wrap">
              {job.contract && (
                <div className="flex items-center gap-1.5 text-sm text-gray-600">
                  <MdWork className="h-4 w-4 text-gray-400" />
                  <span>{job.contract}</span>
                </div>
              )}

              {job.remuneration_min && job.remuneration_max && (
                <div className="flex items-center gap-1.5 text-sm text-gray-600">
                  <MdAttachMoney className="h-4 w-4 text-gray-400" />
                  <span>
                    {job.remuneration_min} - {job.remuneration_max} {job.remuneration_currency} / {job.remuneration_period}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center gap-4 pt-3 border-t border-gray-100">
            <div className="flex items-center gap-1.5 text-xs text-gray-500">
              <MdAccessTime className="h-3.5 w-3.5 text-gray-400" />
              <span>
                {t("candidate_home.posted")} {timeSince(job.created_at)}
              </span>
            </div>
          </div>
        </div>
      </div>
    </Link>
  );
};
